import { APICompsSchema } from "@unit/apis";
import { INFLUENCER_PATH, NOTIFICATION_PATH, notificationConverter, NotificationType } from "@unit/firestore";
import {
  collection,
  query,
  where,
  getDocs,
  onSnapshot,
  doc,
  setDoc,
  getCountFromServer,
  getDoc,
  deleteDoc,
  orderBy,
} from "firebase/firestore";
import { useAtom } from "jotai";
import { useEffect } from "react";
import { useQueryClient } from "react-query";

import { db } from "@/global-state/firebase-settings";
import { authUserAtom, loadingAtom } from "@/global-state/jotai-atom";
import { QueryKeys } from "@/global-state/react-query-keys";

export const useNotification = () => {
  const queryClient = useQueryClient();
  const [authUser] = useAtom(authUserAtom);
  const [, setLoading] = useAtom(loadingAtom);

  useEffect(() => {
    if (!authUser?.id) return;
    onSnapshot(
      collection(db, INFLUENCER_PATH, authUser.id, NOTIFICATION_PATH),
      async () => {
        await queryClient.invalidateQueries(QueryKeys.fetchAllNotificationList);
      },
      (error) => {
        console.log(error);
      },
    );
  }, [authUser?.id]);

  const getNotificationList = async (): Promise<NotificationType[]> => {
    setLoading(true);
    try {
      const collRef = collection(db, `/${INFLUENCER_PATH}/${authUser?.id}/${NOTIFICATION_PATH}`).withConverter(
        notificationConverter,
      );
      const q = query(collRef, where("influencerId", "==", authUser?.id), orderBy("createdAt", "desc"));
      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => doc.data());
    } finally {
      setLoading(false);
    }
  };

  const getNotificationById = async (notificationId: APICompsSchema["UUIDValue"]): Promise<NotificationType | null> => {
    setLoading(true);
    try {
      const docRef = doc(db, `/${INFLUENCER_PATH}/${authUser?.id}/${NOTIFICATION_PATH}`, notificationId).withConverter(
        notificationConverter,
      );
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return docSnap.data();
      } else {
        return null;
      }
    } finally {
      setLoading(false);
    }
  };

  const getUnreadNotificationCount = async (): Promise<number> => {
    setLoading(true);
    try {
      const collRef = collection(db, `/${INFLUENCER_PATH}/${authUser?.id}/${NOTIFICATION_PATH}`).withConverter(
        notificationConverter,
      );
      const q = query(collRef, where("influencerId", "==", authUser?.id), where("openedAt", "==", null));
      const snapshot = await getCountFromServer(q);
      return snapshot.data().count;
    } finally {
      setLoading(false);
    }
  };

  const readNotification = async (notification: NotificationType): Promise<NotificationType> => {
    setLoading(true);
    try {
      const docRef = doc(
        db,
        INFLUENCER_PATH,
        notification.influencerId,
        NOTIFICATION_PATH,
        notification.id,
      ).withConverter(notificationConverter);

      const nowUnixTimeSec = Math.round(new Date().getTime() / 1000);
      const updatedNotification: NotificationType = { ...notification, openedAt: nowUnixTimeSec };
      await setDoc(docRef, updatedNotification);
      return updatedNotification;
    } finally {
      setLoading(false);
    }
  };

  const unreadNotification = async (notification: NotificationType): Promise<NotificationType> => {
    setLoading(true);
    try {
      const docRef = doc(
        db,
        INFLUENCER_PATH,
        notification.influencerId,
        NOTIFICATION_PATH,
        notification.id,
      ).withConverter(notificationConverter);

      const updatedNotification: NotificationType = { ...notification, openedAt: null };
      await setDoc(docRef, updatedNotification);
      return updatedNotification;
    } finally {
      setLoading(false);
    }
  };

  const deleteNotification = async (notification: NotificationType) => {
    setLoading(true);
    try {
      const docRef = doc(db, `/${INFLUENCER_PATH}/${authUser?.id}/${NOTIFICATION_PATH}`, notification.id).withConverter(
        notificationConverter,
      );
      await deleteDoc(docRef);
    } finally {
      setLoading(false);
    }
  };

  return {
    getNotificationList,
    getNotificationById,
    getUnreadNotificationCount,
    readNotification,
    unreadNotification,
    deleteNotification,
  };
};
